'use client';

import * as React from 'react';
import {
  DayPicker,
  getDefaultClassNames,
  type DayButton,
  type DayPickerProps,
} from 'react-day-picker';

import { iconButtonVariants } from '../icon-button/icon-button';
import { cn } from '../../lib/utils';
import { ChevronDown, ChevronLeft, ChevronRight } from '../../icons';
import { Icon } from '../icon/icon';

type DayPickerLocale = DayPickerProps['locale'];

type DayPickerLocaleWithCode = DayPickerLocale & { code?: string };

/**
 * Provides locale code from a DayPicker locale object.
 *
 * Why:
 * - Using `locale?.code` directly is causing TypeScript errors as we have date-fns v2 and v4 typings in the project.
 * - This helper bridges the type conflict at the type level. It does not alter runtime behavior.
 *
 * The DayPicker API and props remain unchanged.
 */
const getLocaleCode = (locale: DayPickerLocale): string | undefined => {
  if (!locale) return undefined;
  const localeWithCode: DayPickerLocaleWithCode = locale;
  return localeWithCode.code;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  locale,
  formatters,
  components,
  ...props
}: DayPickerProps) {
  const defaultClassNames = getDefaultClassNames();
  const localeCode = getLocaleCode(locale);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'group/calendar bg-white p-2 [--cell-radius:var(--radius-md)] [--cell-size:--spacing(10)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
        className,
      )}
      captionLayout={captionLayout}
      locale={locale}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString(localeCode, { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn('flex gap-4 flex-col md:flex-row relative', defaultClassNames.months),
        month: cn('flex flex-col w-full gap-4', defaultClassNames.month),
        nav: cn(
          'flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          iconButtonVariants({ variant: 'white', size: 'sm' }),
          'size-(--cell-size)',
          'aria-disabled:opacity-50 select-none',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          iconButtonVariants({ variant: 'white', size: 'sm' }),
          'size-(--cell-size)',
          'aria-disabled:opacity-50 select-none',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'flex items-center justify-center h-(--cell-size) w-full px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-1.5',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          'relative cn-calendar-dropdown-root rounded-(--cell-radius)',
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn('absolute bg-white inset-0 opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'select-none font-semibold',
          captionLayout === 'label'
            ? 'text-sm'
            : 'cn-calendar-caption-label rounded-(--cell-radius) flex items-center gap-1 text-sm  [&>svg]:text-space-60 [&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        table: 'w-full border-collapse',
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'text-space-60 flex-1 font-semibold text-[0.8rem] select-none',
          defaultClassNames.weekday,
        ),
        week: cn('flex w-full mt-2', defaultClassNames.week),
        week_number_header: cn('select-none w-(--cell-size)', defaultClassNames.week_number_header),
        week_number: cn('text-[0.8rem] select-none text-space-60', defaultClassNames.week_number),
        day: cn(
          'relative w-full rounded-full h-full p-0 text-center group/day aspect-square select-none hover:bg-space-04',
          defaultClassNames.day,
        ),
        range_start: cn(
          'rounded-l-full bg-space-04 relative after:bg-space-04 after:absolute after:inset-y-0 after:w-4 after:right-0 z-0 isolate',
          defaultClassNames.range_start,
        ),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn(
          'rounded-r-full bg-space-04 relative after:bg-space-04 after:absolute after:inset-y-0 after:w-4 after:left-0 z-0 isolate',
          defaultClassNames.range_end,
        ),
        today: cn(
          'bg-space-08 text-space rounded-full data-[selected=true]:rounded-full',
          'hover:bg-space-08',
          defaultClassNames.today,
        ),
        outside: cn('text-space-40 aria-selected:text-space-40', defaultClassNames.outside),
        disabled: cn('text-space-40 hover:bg-transparent', defaultClassNames.disabled),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => (
          <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />
        ),
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return <Icon icon={ChevronLeft} className={cn('size-4', className)} {...props} />;
          }

          if (orientation === 'right') {
            return <Icon icon={ChevronRight} className={cn('size-4', className)} {...props} />;
          }

          return <Icon icon={ChevronDown} className={cn('size-4', className)} {...props} />;
        },
        DayButton: ({ ...props }) => <CalendarDayButton localeCode={localeCode} {...props} />,
        WeekNumber: ({ children, ...props }) => (
          <td {...props}>
            <div className="flex size-(--cell-size) items-center justify-center text-center">
              {children}
            </div>
          </td>
        ),
        ...components,
      }}
      weekStartsOn={1}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  localeCode,
  ...props
}: React.ComponentProps<typeof DayButton> & { localeCode?: string }) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers['focused']) ref.current?.focus();
  }, [modifiers]);

  return (
    <button
      ref={ref}
      data-day={day.date.toLocaleDateString(localeCode)}
      data-selected-single={
        modifiers['selected'] &&
        !modifiers['range_start'] &&
        !modifiers['range_end'] &&
        !modifiers['range_middle']
      }
      data-range-start={modifiers['range_start']}
      data-range-end={modifiers['range_end']}
      data-range-middle={modifiers['range_middle']}
      className={cn(
        // layout + spacing
        'relative isolate z-10 flex aspect-square size-auto w-full min-w-(--cell-size) flex-col justify-center gap-1 border-0 leading-none',
        'rounded-full',
        // typography
        'text-[14px] font-normal [&>span]:text-xs [&>span]:opacity-70',
        // single date selection
        'data-[selected-single=true]:rounded-full data-[selected-single=true]:bg-indigo-crush data-[selected-single=true]:font-semibold data-[selected-single=true]:text-white',
        // range selection - middle
        'data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-indigo-crush-20 data-[range-middle=true]:text-space',
        // range selection - start
        'data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:rounded-l-full data-[range-start=true]:bg-indigo-crush data-[range-start=true]:text-white',
        'data-[range-start=true]:font-semibold',
        // range selection - end
        'data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:rounded-r-full data-[range-end=true]:bg-indigo-crush data-[range-end=true]:text-white',
        'data-[range-end=true]:font-semibold',
        // focus states
        // "group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-focus-ring group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-focus-ring/50",
        // focus state with focus-visible
        'focus-visible:relative focus-visible:z-10 focus-visible:border-focus-ring focus-visible:ring-[3px] focus-visible:ring-focus-ring/50',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
